/** /signin — teacher sign-in against the auth service. In the dev open mode services accept
 * requests without a token, so this page is only reached when a 401 was actually returned. */
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { get } from "../api/client";
import { serviceUrl } from "../api/base";

import { useSession } from "../auth/SessionContext";
import { ResendConfirmation } from "../auth/ResendConfirmation";
import { signIn } from "../auth/session";
import { isUnconfirmed } from "../auth/signupRules";
import { useSignupPolicy } from "../auth/useSignupPolicy";
import { product } from "../product";
import { Button, PasswordInput } from "../ui";

export function SignIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reload } = useSession();
  const { policy } = useSignupPolicy();
  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // the address a self-signed-up teacher must confirm before the issuer gives a token
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    setUnconfirmed(null);
    try {
      await signIn(id, pw, true);
      await reload();
      void navigate({ to: "/t" });
    } catch (e) {
      if (isUnconfirmed(e)) {
        setUnconfirmed(id.trim());
        setError(t("signin.unconfirmed"));
      } else {
        setError(e instanceof Error ? e.message : t("signin.failed"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-6 p-6">
      <h1 className="wordmark text-3xl" translate="no">{product.name}</h1>
      <p className="-mt-3 text-[color:var(--ink-2)]">{t("signin.lede")}</p>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t("signin.identifier")}</span>
          <input className="field py-3" name="identifier" value={id} onChange={(e) => setId(e.target.value)} autoComplete="username" spellCheck={false} autoCapitalize="none" required />
        </label>
        <PasswordInput label={t("signin.password")} name="password" value={pw} onChange={setPw} autoComplete="current-password" required />
        {error && <p role="alert" className="text-sm" style={{ color: "var(--danger)" }}>{error}</p>}
        {unconfirmed && <ResendConfirmation email={unconfirmed} />}
        <Button type="submit" block loading={busy}>{t("signin.cta")}</Button>
        <SsoProviders />
      </form>
      {policy === "open" && (
        <p className="text-sm">
          {t("signin.new")}{" "}
          <Link to="/t/signup" className="inline-flex min-h-[var(--control-h)] items-center font-medium underline underline-offset-4 hover:decoration-2">
            {t("teacherSignup.cta")}
          </Link>
        </p>
      )}
    </main>
  );
}


/** STORY-097: institution sign-in through providers an admin registered. */
function SsoProviders() {
  const { t } = useTranslation();
  const providers = useQuery({
    queryKey: ["sso", "providers"],
    queryFn: () => get<{ provider_id: string; name: string; login_path: string }[]>("/integrations/v1/sso/providers"),
    retry: false,
  });
  // a gateway that answers with a page instead of JSON (a misrouted origin) must not crash sign-in
  const list = Array.isArray(providers.data) ? providers.data : [];
  if (!list.length) return null;
  return (
    <div className="mt-2 flex flex-col gap-2">
      <p className="text-center text-xs text-[color:var(--ink-2)]">{t("signin.sso")}</p>
      {list.map((p) => (
        <a key={p.provider_id} className="inline-flex min-h-[var(--control-h)] items-center justify-center rounded-[var(--radius-control)] border px-4 text-sm font-medium hover:bg-[color:var(--surface-2)]" href={`${serviceUrl("/integrations")}${p.login_path}?next=${encodeURIComponent("/t")}`}>
          {t("signin.ssoWith", { name: p.name })}
        </a>
      ))}
    </div>
  );
}
